import React from 'react';
import moment from 'moment-timezone';

import Strings from '../Strings';
import { DATE_MONTH_DAY_YEAR, RPM_PATIENT_STATUS } from '../constants';
import { getVitalsLabelsFromEnrollment } from './enrollmentFields';

export const ACTIVATION_METHODS = {
  FirstEvent: 'FirstEvent',
  OnDayAfterFirstEvent: 'OnDayAfterFirstEvent',
  Manual: 'Manual',
};

const activationMethodPriority = [
  ACTIVATION_METHODS.FirstEvent,
  ACTIVATION_METHODS.OnDayAfterFirstEvent,
  ACTIVATION_METHODS.Manual,
];

export const getOrderedActivationMethods = activationMethods => {
  const methods = Array.isArray(activationMethods) ? [...new Set(activationMethods.filter(Boolean))] : [];
  return activationMethodPriority
    .filter(method => methods.includes(method))
    .concat(methods.filter(method => !activationMethodPriority.includes(method)));
};

export const getDefaultActivationMethod = activationMethods => {
  return getOrderedActivationMethods(activationMethods)[0] || ACTIVATION_METHODS.Manual;
};

export const RTM = 'rtm';
export const RPM = 'rpm';
export const CCM = 'ccm';
export const PCM = 'pcm';
export const CTAM = 'ctam';
export const ECKM = 'eckm';
export const CKM = 'ckm';
export const MSK = 'msk';
export const BH = 'bh';
export const RTMMusculoskeletal = 'musculoskeletal';
export const RTMRespiratory = 'respiratory';
export const RTMBehavioralHealth = 'behavioralHealth';
export const CCMBasic = 'basic';
export const CCMComplex = 'complex';
export const ACCESS_PROGRAMS = Object.freeze(['eCKM', 'CKM', 'MSK', 'BH']);
export const REGISTRATION_TYPES = Object.freeze({
  careManagement: 'careManagement',
  access: 'access',
  clinicalTrials: 'clinicalTrials',
});
export const programs = { RTM, RPM, CCM, PCM, CTAM, eCKM: ECKM, CKM, MSK, BH };
export const subprograms = {
  RTMMusculoskeletal,
  RTMRespiratory,
  RTMBehavioralHealth,
  RPM,
  CCMBasic,
  CCMComplex,
  PCM,
  CTAM,
  eCKM: ECKM,
  CKM,
  MSK,
  BH,
};

export const isAdherenceMonitoringProgram = program => program === RTM || program === CTAM;
export const isAccessProgram = program => ACCESS_PROGRAMS.includes(program);

export const getRegistrationTypeForProgram = program => (
  isAccessProgram(program)
    ? REGISTRATION_TYPES.access
    : program === 'CTAM'
    ? REGISTRATION_TYPES.clinicalTrials
    : REGISTRATION_TYPES.careManagement
);

export const getSupportedRegistrationTypes = programsForEnrollment => {
  const supportedPrograms = Array.isArray(programsForEnrollment) ? programsForEnrollment : [];
  if (supportedPrograms.length === 0) {
    return Object.values(REGISTRATION_TYPES);
  }

  return Object.values(REGISTRATION_TYPES).filter(registrationType => (
    supportedPrograms.some(program => getRegistrationTypeForProgram(program) === registrationType)
  ));
};

export const getProgramAndSubprogramString = program => {
  const pr = programs[program.program];
  const subprogram = program.subprogram != program.program ? subprograms[program.subprogram] : null;

  return `${Strings.programs[pr]}${subprogram ? ` - ${Strings.subprograms[pr][subprogram]}` : ''}`;
};

export const datesText = program => {
  if (!program.enrollmentStart && !program.enrollmentEnd) {
    return '';
  }
  return `${program.enrollmentStart ? moment(program.enrollmentStart).format(DATE_MONTH_DAY_YEAR) : '?'} - ${
    program.enrollmentEnd ? moment(program.enrollmentEnd).format(DATE_MONTH_DAY_YEAR) : '?'
  }`;
};

const careProviderText = program => {
  if (!program.careProvider) {
    return '';
  }
  return `${program.careProvider.name} (${program.careProvider.organization})`;
};

const renderProgram = program => {
  const text = getProgramAndSubprogramString(program);
  const dates = datesText(program) ? `${Strings.activationStartEnd}: ${datesText(program)}` : '';
  const activateAt = program.activationMethod !== ACTIVATION_METHODS.Manual && program.status === RPM_PATIENT_STATUS.enrolled
  ? `${Strings.capPatient.enrollmentWillActivate}: ${Strings.capPatient.enrollmentActivationModesEnum[program.activationMethod]}`
  : '';
  const provider = careProviderText(program);
  const providerText = provider ? `${Strings.capPatient.careProvider}: ${provider}` : '';
  const conditionText = program.conditionTitle ? `${Strings.condition}: ${program.conditionTitle}` : '';
  const medications = program.medications && program.medications.length > 0 ? `${Strings.medications}: ${program.medications.map(m => m.name).join(', ')}` : '';
  const vitals = program.allowedVitals ? `${Strings.vitals}: ${getVitalsLabelsFromEnrollment(program, { withIncluded: true })}` : '';
  const array = [dates, activateAt, providerText, conditionText, medications, vitals];
  const tooltip = array.filter(t => t).join('<br/>');
  return (
    <div
      key={program.enrollmentId}
      className="icon-and-text-container"
      data-tooltip-html={tooltip}
      data-tooltip-id={tooltip ? 'tooltip' : undefined}
    >
      {(program.enrollmentStart || program.activationMethod === ACTIVATION_METHODS.FirstEvent || program.activationMethod === ACTIVATION_METHODS.OnDayAfterFirstEvent)
        && program.status === RPM_PATIENT_STATUS.enrolled
        && <div className="icon-container tick" data-tooltip-id="tooltip" data-tooltip-content={Strings.capPatient.enrollmentIsReadyToStart} />}
      {program.enrollmentEnd && (
        <div
          className="icon-container warning"
          data-tooltip-content={Strings.capPatient.enrollmentEndDateIsSet}
          data-tooltip-id="tooltip"
        />
        )}
      {text}
    </div>
  );
};

export const renderPrograms = patientEnrollment => {
  if (!patientEnrollment || !patientEnrollment.enrollments) {
    return '-';
  }
  const patientPrograms = getProgramsFromPatient({ patientEnrollment });
  return patientPrograms.map(program => renderProgram(program));
};

export const renderShipmentEnrollment = (program, shipmentState) => {
  const text = getProgramAndSubprogramString(program);
  return (
    <div className="icon-and-text-container">
      {program.status === 'stopped' && ['created', 'new'].includes(shipmentState) && (
        <div
          className="icon-container warning"
          data-tooltip-content={Strings.capPatient.enrollmentIsCancelled}
          data-tooltip-id="tooltip"
        />
        )}
      {text}
    </div>
  );
};

export const flatProgramsSubprogramsList = [
  // id - for selects
  {
    name: RTM,
    subprogram: RTMMusculoskeletal,
    displayText: `${Strings.programs.rtm} - ${Strings.subprograms.rtm.musculoskeletal}`,
    id: 0,
  },
  {
    name: RTM,
    subprogram: RTMRespiratory,
    displayText: `${Strings.programs.rtm} - ${Strings.subprograms.rtm.respiratory}`,
    id: 1,
  },
  {
    name: RTM,
    subprogram: RTMBehavioralHealth,
    displayText: `${Strings.programs.rtm} - ${Strings.subprograms.rtm.behavioralHealth}`,
    id: 2,
  },
  { name: RPM, subprogram: RPM, displayText: Strings.programs.rpm, id: 3 },
  {
    name: CCM,
    subprogram: CCMBasic,
    displayText: `${Strings.programs.ccm} - ${Strings.subprograms.ccm.basic}`,
    id: 4,
  },
  {
    name: CCM,
    subprogram: CCMComplex,
    displayText: `${Strings.programs.ccm} - ${Strings.subprograms.ccm.complex}`,
    id: 5,
  },
  { name: PCM, subprogram: PCM, displayText: Strings.programs.pcm, id: 6 },
  { name: CTAM, subprogram: CTAM, displayText: Strings.programs.ctam, id: 7 },
  { name: ECKM, subprogram: ECKM, displayText: Strings.programs.eckm, id: 8 },
  { name: CKM, subprogram: CKM, displayText: Strings.programs.ckm, id: 9 },
  { name: MSK, subprogram: MSK, displayText: Strings.programs.msk, id: 10 },
  { name: BH, subprogram: BH, displayText: Strings.programs.bh, id: 11 },
];

export const programsAndSubprograms = [
  { name: RTM, displayText: Strings.programs.rtm, type: 'program', value: 'RTM' },
  { name: RTMMusculoskeletal, displayText: `${Strings.programs.rtm} - ${Strings.subprograms.rtm.musculoskeletal}`, type: 'subprogram', value: 'RTMMusculoskeletal' },
  { name: RTMRespiratory, displayText: `${Strings.programs.rtm} - ${Strings.subprograms.rtm.respiratory}`, type: 'subprogram', value: 'RTMRespiratory' },
  { name: RTMBehavioralHealth, displayText: `${Strings.programs.rtm} - ${Strings.subprograms.rtm.behavioralHealth}`, type: 'subprogram', value: 'RTMBehavioralHealth' },
  { name: RPM, displayText: Strings.programs.rpm, type: 'program', value: 'RPM' },
  { name: CCM, displayText: Strings.programs.ccm, type: 'program', value: 'CCM' },
  { name: CCMBasic, displayText: `${Strings.programs.ccm} - ${Strings.subprograms.ccm.basic}`, type: 'subprogram', value: 'CCMBasic' },
  { name: CCMComplex, displayText: `${Strings.programs.ccm} - ${Strings.subprograms.ccm.complex}`, type: 'subprogram', value: 'CCMComplex' },
  { name: PCM, displayText: Strings.programs.pcm, type: 'program', value: 'PCM' },
  { name: CTAM, displayText: Strings.programs.ctam, type: 'program', value: 'CTAM' },
  { name: ECKM, displayText: Strings.programs.eckm, type: 'program', value: 'eCKM' },
  { name: CKM, displayText: Strings.programs.ckm, type: 'program', value: 'CKM' },
  { name: MSK, displayText: Strings.programs.msk, type: 'program', value: 'MSK' },
  { name: BH, displayText: Strings.programs.bh, type: 'program', value: 'BH' },
];

export const getSupportedAccessPrograms = patient => {
  const supportedPrograms = patient?.patientEnrollment?.supportedPrograms;
  if (!Array.isArray(supportedPrograms) || supportedPrograms.length === 0) {
    return [...ACCESS_PROGRAMS];
  }

  return supportedPrograms.filter(isAccessProgram);
};

const ACCESS_ELIGIBILITY_IN_FLIGHT_STATUSES = ['pending', 'sent', 'polling', 'dispatching', 'processing'];

export const isAccessRegistrationPending = enrollment => (
  isAccessProgram(enrollment?.program)
  && enrollment?.accessEligibility?.requestedAction === 'register'
  && ACCESS_ELIGIBILITY_IN_FLIGHT_STATUSES.includes(enrollment.accessEligibility.status)
);

export const isAccessEligibilityPending = enrollment => (
  isAccessProgram(enrollment?.program)
  && enrollment?.accessEligibility
  && ACCESS_ELIGIBILITY_IN_FLIGHT_STATUSES.includes(enrollment.accessEligibility.status)
);

export const hasAccessEligibilityInFlight = patient => Object.values(
  patient?.patientEnrollment?.enrollments || {},
).some(isAccessEligibilityPending);

// Alignment shares the queue lifecycle with eligibility, so the same statuses
// count as in flight.
const ACCESS_ALIGNMENT_IN_FLIGHT_STATUSES = ACCESS_ELIGIBILITY_IN_FLIGHT_STATUSES;

export const isAccessAlignmentPending = enrollment => (
  isAccessProgram(enrollment?.program)
  && enrollment?.accessAlignment
  && ACCESS_ALIGNMENT_IN_FLIGHT_STATUSES.includes(enrollment.accessAlignment.status)
);

export const hasAccessAlignmentInFlight = patient => Object.values(
  patient?.patientEnrollment?.enrollments || {},
).some(isAccessAlignmentPending);

export const getProgramForApi = name => {
  return Object.keys(programs).find(key => programs[key] === name);
};

export const getSubprogramForApi = name => {
  return Object.keys(subprograms).find(key => subprograms[key] === name);
};

export const getProgramDisplayName = (program, subprogram = null) => {
  if (!program) {
    return '';
  }
  const programKey = programs[program] || program;
  const subprogramKey = subprograms[subprogram] || subprogram;
  const subprogramName = Strings.subprograms[programKey]?.[subprogramKey];
  return `${Strings.programs[programKey] || program}${
    subprogram && subprogramKey !== programKey && subprogramName ? ` - ${subprogramName}` : ''
  }`;
};

export const getProgramsFromPatient = (patientDetails, statesToFilterBy = [RPM_PATIENT_STATUS.activated, RPM_PATIENT_STATUS.enrolled]) => {
  if (!patientDetails?.patientEnrollment?.enrollments) {
    return [];
  }
  const ret = [];
  Object.entries(patientDetails.patientEnrollment.enrollments).forEach(([key, v]) => {
    if (statesToFilterBy.includes(v.status)) {
      const programDisplayName = getProgramDisplayName(v.program, v.subprogram) || Strings.capPatient.noProgram;
      ret.push({
        ...v,
        enrollmentId: key,
        name: v.program,
        isDefault: v.program === patientDetails.patientEnrollment.defaultProgram,
        displayName: `${programDisplayName}${v.conditionTitle ? ` - ${v.conditionTitle}` : ''}`,
        display: (addCondition, addStatus) => {
          const condition = addCondition ? ` - ${v.conditionTitle}` : '';
          const status = addStatus ? ` - ${v.status}` : '';
          return `${programDisplayName}${condition}${status}`;
        },
      });
    }
  });
  return ret;
};

export const CptCodes = [
  { program: CCM, code: "99439", label: "CPT 99439" },
  { program: CCM, code: "99487", label: "CPT 99487" },
  { program: CCM, code: "99489", label: "CPT 99489" },
  { program: PCM, code: "99490", label: "CPT 99490" },
  { program: PCM, code: "99491", label: "CPT 99491" },
  { program: RPM, code: "99453", label: "CPT 99453" },
  { program: RPM, code: "99454", label: "CPT 99454" },
  { program: RPM, code: "99457", label: "CPT 99457" },
  { program: RPM, code: "99458", label: "CPT 99458" },
  { program: RTM, code: "98975", label: "CPT 98975" },
  { program: RTM, code: "98976", label: "CPT 98976" },
  { program: RTM, code: "98977", label: "CPT 98977" },
  { program: RTM, code: "98978", label: "CPT 98978" },
  { program: RTM, code: "98980", label: "CPT 98980" },
  { program: RTM, code: "98981", label: "CPT 98981" },
  { program: PCM, code: "99424", label: "CPT 99424" },
  { program: PCM, code: "99425", label: "CPT 99425" },
  { program: PCM, code: "99426", label: "CPT 99426" },
  { program: PCM, code: "99427", label: "CPT 99427" },
]
